import { X, RefreshCw } from "lucide-react";
import MediaFrame from "@/components/common/MediaFrame";
import { t } from "@/i18n";

/**
 * ReferenceChips
 * Presentación de referencias adjuntas para la generación:
 * - Vistas seleccionadas de personajes (@personaje · vista) con acción para cambiar vista o retirar.
 * - Activos manuales de biblioteca / subidos.
 * - Referencias del workspace vinculadas.
 */
const ReferenceChips = ({
  references = [],
  selectedIds = [],
  onToggle,
  assetById = {},
  extraAssets = [],
  onRemoveExtra,
  characterReferences = [],
  onRemoveCharacterRef,
  onChangeCharacterRef,
}) => (
  <div className="flex flex-wrap items-center gap-2">
    {/* Referencias provenientes de personajes (@personaje) */}
    {characterReferences.map((cr) => {
      const asset = assetById[cr.assetId];
      return (
        <div
          key={cr.assetId}
          data-testid={`tool-ref-char-${cr.assetId}`}
          className="inline-flex items-center gap-1.5 rounded-lg border border-purple-500/40 bg-purple-500/10 py-1 pl-1.5 pr-1.5 text-xs text-purple-200 shadow-sm"
        >
          <MediaFrame
            asset={asset}
            fit="contain"
            className="h-6 w-6 shrink-0 rounded border border-purple-400/30 bg-black/50"
            showBadge={false}
          />
          <div className="flex flex-col min-w-0 pr-1">
            <span className="font-mono text-[11px] font-semibold text-purple-100 truncate">
              @{cr.characterHandle}
            </span>
            <span className="text-[10px] text-purple-300/80 truncate">
              {cr.viewLabel || t.characters?.view || "Vista"}
            </span>
          </div>

          {onChangeCharacterRef && (
            <button
              type="button"
              data-testid={`tool-ref-char-change-${cr.assetId}`}
              onClick={(e) => {
                e.stopPropagation();
                onChangeCharacterRef(cr);
              }}
              className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 transition-colors"
              title={t.tools?.changeView || "Cambiar vista"}
            >
              {t.tools?.changeView || "Cambiar"}
            </button>
          )}

          {onRemoveCharacterRef && (
            <button
              type="button"
              data-testid={`tool-ref-char-remove-${cr.assetId}`}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveCharacterRef(cr.assetId);
              }}
              title={t.common?.remove || "Quitar"}
              className="rounded p-0.5 text-purple-300/80 hover:bg-purple-500/25 hover:text-white transition-colors"
            >
              <X size={12} className="shrink-0" />
            </button>
          )}
        </div>
      );
    })}

    {/* Referencias manuales añadidas desde biblioteca o subidas */}
    {extraAssets.map((a) => (
      <div
        key={a.id}
        data-testid={`tool-ref-asset-${a.id}`}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[#f5b544]/50 bg-[#f5b544]/10 py-1 pl-1.5 pr-1.5 text-xs text-[#f5b544] shadow-sm"
      >
        <MediaFrame
          asset={a}
          fit="contain"
          className="h-6 w-6 shrink-0 rounded border border-[#f5b544]/30 bg-black/50"
          showBadge={false}
        />
        <span className="max-w-[140px] truncate text-xs font-medium text-[#ededed]">
          {a.label || a.filename || t.common?.untitled || "Activo"}
        </span>
        {onRemoveExtra && (
          <button
            type="button"
            data-testid={`tool-ref-asset-remove-${a.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveExtra(a.id);
            }}
            title={t.common?.remove || "Quitar"}
            className="rounded p-0.5 text-[#f5b544]/80 hover:bg-[#f5b544]/20 hover:text-[#f5b544] transition-colors"
          >
            <X size={12} className="shrink-0" />
          </button>
        )}
      </div>
    ))}

    {/* Referencias del catálogo de referencias del workspace */}
    {references.map((r) => {
      const on = selectedIds.includes(r.id);
      return (
        <button
          key={r.id}
          type="button"
          data-testid={`tool-ref-${r.id}`}
          onClick={() => onToggle?.(r.id)}
          className={`inline-flex items-center gap-1.5 rounded-lg border py-1 pl-1.5 pr-2 font-mono text-xs transition-colors ${
            on
              ? "border-[#f5b544] bg-[#f5b544]/15 text-[#f5b544]"
              : "border-[#303030] text-[#a3a3a3] hover:text-[#f4f1ec] hover:border-[#444]"
          }`}
        >
          <MediaFrame
            asset={assetById[r.assetId]}
            fit="contain"
            className="h-5 w-5 shrink-0 rounded border border-white/10"
            showBadge={false}
          />
          <span>{r.label}</span>
          {on && <X size={11} className="shrink-0 ml-0.5 opacity-80" />}
        </button>
      );
    })}

    {references.length === 0 &&
      extraAssets.length === 0 &&
      characterReferences.length === 0 && (
        <span className="text-xs text-[#737373] italic">
          {t.tools?.referencesEmpty || "Sin referencias seleccionadas"}
        </span>
      )}
  </div>
);

export default ReferenceChips;
